/**
 * Wind Analysis Constants
 *
 * Centralized configuration for wind analysis, leg detection, tack/jibe recognition,
 * and related calculations. All magic numbers have been extracted here for easy
 * maintenance and documentation.
 */

export const WIND_ANALYSIS_CONSTANTS = {
  // ===== LEG DETECTION =====

  /**
   * Number of points to analyze in sliding window for direction changes
   * A larger window creates more stable legs but may miss quick direction changes
   */
  WINDOW_SIZE: 8,

  /**
   * Maximum consecutive invalid points before breaking a leg
   * Allows for GPS glitches without breaking continuity
   */
  MAX_SKIPPED_POINTS: 5,

  /**
   * Minimum number of valid points (speed > 0) required to consider a leg
   * Filters out very short segments that aren't meaningful
   */
  MIN_LEG_POINTS: 50,

  /**
   * Minimum duration in seconds to display a leg (2 minutes)
   * Shorter legs are calculated but not shown in the UI
   */
  MIN_LEG_DURATION_SECONDS: 120,

  /**
   * Minimum duration in seconds to display a run (30 seconds)
   * Runs are subdivisions within legs (between maneuvers)
   * Shorter runs are filtered out to reduce noise
   */
  MIN_RUN_DURATION_SECONDS: 30,

  /**
   * Percentage threshold for direction change (0-1)
   * 0.6 = 60% of points in window must agree to change direction
   */
  DIRECTION_CHANGE_THRESHOLD: 0.6,

  // ===== SPEED & BEARING THRESHOLDS =====

  /**
   * Minimum speed (km/h) for reliable GPS bearing data
   * Below this speed, GPS bearing becomes too unreliable
   */
  MIN_SPEED_FOR_BEARING_KMH: 3,

  /**
   * Minimum True Wind Angle (TWA) for polar chart calculations
   * Represents the "no-go zone" - most boats can't sail closer than 40-45° to the wind
   * Reference: https://www.leopardcatamarans.com/news-and-events/blog/understanding-polar-diagrams-sailing-number-maximize-performance
   * Typical close-hauled angles:
   * - Cruising sailboats/catamarans: 45-50° TWA
   * - Performance cruisers: 40-45° TWA
   * - Racing boats: 35-40° TWA (ideal conditions)
   */
  MIN_SAILABLE_TWA: 40,

  /**
   * TWA threshold (degrees) that separates upwind from downwind
   * TWA < 90° = upwind (close-hauled)
   * TWA >= 90° = downwind (broad reach/run)
   */
  UPWIND_DOWNWIND_THRESHOLD: 90,

  // ===== TACK/JIBE DETECTION =====

  /**
   * Number of consecutive points to analyze for tack/jibe detection
   * Must be at least 9 for the algorithm to work properly
   */
  TACK_JIBE_POINTS_TO_USE: 10,

  /**
   * Minimum angle change (degrees) to consider a maneuver
   * Filters out minor course corrections
   */
  TACK_JIBE_MIN_ANGLE: 5,

  /**
   * Maximum angle change (degrees) to consider a maneuver.
   * Real head-to-wind tacks can reach ~180°. Anything strictly above this is
   * likely a doubled-back GPS sample. Bumped from 150 to 175 so that
   * near-180° tacks (the "almost 0° angle" turns the user perceives because
   * the heading reverses) are accepted instead of clipped.
   */
  TACK_JIBE_MAX_ANGLE: 175,

  /**
   * Sustained-heading post-filter applied AFTER the in-window detector.
   *
   * The detector slides a 10-point window over the track and fires on any
   * window that crosses the wind axis with a >=5° in-window angle change.
   * On near-straight upwind runs that's too sensitive: GPS noise produces
   * a small wiggle that happens to straddle the wind axis and gets flagged
   * as a tack/jibe even though the rider held essentially the same heading
   * before and after. Users see clusters of false maneuvers stacked on a
   * straight leg (see `doc/vmg/GPX/too many gybes.gpx` and
   * `doc/vmg/GPX/angle to check.gpx`).
   *
   * To reject those, we measure the rider's sustained heading on a stable
   * leg BEFORE and AFTER each detected maneuver:
   *   - skip the immediate `_LAG_SEC` seconds around the turn (which carry
   *     the maneuver's own arc dynamics and are noisy by definition)
   *   - then collect `_DURATION_SEC` seconds of "riding" samples
   *   - require the bearing change between those two legs to be at least
   *     `_MIN_ANGLE` degrees
   *
   * Real maneuvers always produce a sustained heading change well above
   * this threshold (jibes typically 80-150°, tacks 60-180°). Phantom
   * maneuvers from GPS noise on a straight line produce < 30° of sustained
   * change. 40° leaves a generous margin.
   *
   * When the maneuver is at the start/end of the track and we can't form
   * a complete pre/post leg, the filter abstains and KEEPS the maneuver
   * (never drop real maneuvers we can't verify).
   */
  TACK_JIBE_SUSTAINED_LAG_SEC: 5,
  TACK_JIBE_SUSTAINED_DURATION_SEC: 10,
  TACK_JIBE_SUSTAINED_MIN_ANGLE: 40,

  /**
   * Arc-sample classifier override.
   *
   * The default tack/jibe classifier picks the wind side via a path-midpoint
   * reconciled from the in-window initial/mid/final bearings. With only three
   * coarse bearings it gets fooled by short, fast maneuvers (a wing jibe is
   * over in 3-4 GPS samples), so the short window's mid-bearing is dominated
   * by the pre- or post-leg and the path-mid lands on the wrong side of the
   * wind axis. The user-reported "wrong tack, is jibe" file shows this
   * clearly: the rider's heading passes within 8° of dead leeward during the
   * turn, yet the 3-bearing path-mid puts the apex on the windward side.
   *
   * The fix samples the rider's GPS heading between every consecutive pair
   * of points across the maneuver window (`MIN_SPEED_FOR_BEARING_KMH` to
   * suppress low-speed GPS noise) and records the closest approach to the
   * windward (upwind) and leeward (downwind) axes. The maneuver "swept
   * through" whichever axis it got closer to.
   *
   * To stay conservative the override has TWO tiers and only flips when one
   * triggers:
   *   1. VERY-STRONG tier: the rider's heading came within
   *      `_VERY_STRONG_DEGREES` of one wind axis (≈head-to-wind or
   *      dead-downwind). A close pass like that only happens by sweeping
   *      through that axis, so we accept a small `_VERY_STRONG_MARGIN_DEGREES`
   *      margin against the other axis (the other axis can still be
   *      modestly close on near-180° turns whose endpoints sit between the
   *      two wind axes).
   *   2. STRONG tier: the rider got within `_STRONG_DEGREES` of one axis AND
   *      stayed at least `_STRONG_MARGIN_DEGREES` farther from the other.
   *      This catches turns where the apex didn't quite kiss the axis but
   *      the asymmetry is clear.
   *
   * Maneuvers where the heading passes near BOTH axes (e.g. >180° spins or
   * end-of-track stalls whose bearings are GPS noise) hit neither tier and
   * fall back to the baseline classifier.
   *
   * Sailing boats use stricter very-strong/strong thresholds because boat
   * tacks always pass through head-to-wind and never through dead downwind,
   * so any ambiguous pass through leeward is almost certainly the result
   * of a wide jibe arc — we want to keep that as a jibe.
   */
  TACK_JIBE_ARC_VERY_STRONG_DEGREES: 18,
  TACK_JIBE_ARC_VERY_STRONG_MARGIN_DEGREES: 8,
  TACK_JIBE_ARC_STRONG_DEGREES: 30,
  TACK_JIBE_ARC_STRONG_MARGIN_DEGREES: 25,
  TACK_JIBE_ARC_SAILING_VERY_STRONG_DEGREES: 12,
  TACK_JIBE_ARC_SAILING_STRONG_DEGREES: 25,

  /**
   * Required closest approach to either wind axis (in degrees) for a
   * maneuver to count as a real tack or jibe.
   *
   * A real tack sweeps the rider's heading through (or very near) the
   * windward axis (`heading ≈ wind direction`). A real jibe sweeps through
   * (or very near) the leeward axis. If the rider never gets within this
   * many degrees of EITHER axis during the detection window, they did not
   * actually cross the wind — what the 10-point detector caught was a
   * stall, a crash, a long broad-reach scallop, or a "bear away" where the
   * rider just changed point-of-sail.
   *
   * The user-reported "Gybe controvento" (upwind jibe) and "not a tack"
   * files are dominated by this failure mode: the rider was riding on a
   * reach, then stalled, and the drift produced a 170°+ in-window heading
   * change far from either wind axis. The maneuver shouldn't appear in
   * the list of tacks/jibes.
   *
   * Set to 45° to be conservative: every validated jibe in the regression
   * GPX files (notably `too many gybes.gpx`) has min axis approach < 43°,
   * so the filter only catches clear non-maneuvers.
   */
  TACK_JIBE_MIN_AXIS_APPROACH_DEGREES: 45,

  /**
   * High-angle maneuvers that clearly crossed the wind axis can fail the
   * axis-approach filter when the rider never pinches to head-to-wind (common
   * on windsurf upwind beats). Skip the axis filter when the in-window
   * detected angle is at least this large.
   */
  TACK_JIBE_AXIS_EXEMPT_ANGLE_DEG: 90,

  /**
   * Slow-through-turn success exemption. A rider can lose a lot of speed
   * mid-tack while still completing a real heading change; requiring
   * avgSpeed > sport threshold during the turn drops those maneuvers.
   */
  TACK_JIBE_SUCCESS_ANGLE_EXEMPT_DEG: 55,

  /**
   * Weak phantom drop: when the sustained-heading filter cannot measure
   * either leg and the in-window angle is below this, drop the detection.
   * Keeps early-track abstentions for real maneuvers we cannot verify.
   */
  TACK_JIBE_PHANTOM_WEAK_UNVERIFIED_MAX_ANGLE: 25,

  /**
   * Straight-leg phantom rejection. If more than this fraction of samples
   * inside the detection window still match the pre-leg bearing within
   * `_STRAIGHT_LEG_MATCH_DEG`, the rider had not started turning yet and
   * the endpoint angle is misleading (GPS wobble on a straight reach).
   */
  TACK_JIBE_STRAIGHT_LEG_MATCH_DEG: 20,
  TACK_JIBE_STRAIGHT_LEG_MAX_FRACTION: 0.42,

  /**
   * Window-alignment thresholds.
   *
   * The 10-point sliding window in `parseMovements` commits a maneuver
   * whenever the window crosses the wind axis with a sustained angle
   * change, but the window's start/end indices are NOT the actual U-turn
   * boundaries — they're just the first window that satisfied the
   * crossing test. For short maneuvers (≤ 10 points) the window pads
   * the U with pre-leg samples on one side; for long maneuvers (> 10
   * points, common in slow boats or wide carves) the window slices a
   * fixed 10-point chunk out of the middle of the U and the marker
   * midpoint drifts off the apex.
   *
   * Both produce the same user complaint: "the recognition is 2-4
   * points too early/late, the U shape is clearly here but you're
   * highlighting the wrong stretch." See `tools/measure-window-skew.mjs`
   * for empirical measurements on the user-supplied GPX files.
   *
   * `alignManeuverWindowsToTurn` walks outward from the detection
   * window to find the actual U boundaries:
   *   - `_DIVERGENCE_DEG`: how far the local bearing must deviate from
   *     the sustained pre-leg before we declare "the turn has started"
   *     (and symmetrically how far from the post-leg means "still
   *     turning"). 25° is conservative: GPS noise on a stable leg is
   *     typically <10°, while a real turn rotates >60° total.
   *   - `_CONVERGE_DEG`: how close the local bearing must get to the
   *     sustained post-leg before we declare "the turn is over".
   *     Slightly tighter than divergence so we don't mark the rider as
   *     "settled" on the post-leg while they're still hooking the last
   *     few degrees of the carve.
   *   - `_STABLE_RUN`: number of consecutive close-to-post-leg samples
   *     required to confirm the U is done. Single-sample dips back to
   *     the post heading are not reliable enough — we want at least one
   *     pair so a flicker can't end the maneuver early.
   *   - `_MAX_EXPAND_BACKWARD` / `_MAX_EXPAND_FORWARD`: clamp how far
   *     the alignment can move start/end indices outside the original
   *     detection window. Backward is small because the detector almost
   *     never fires late on the entry; forward is generous because the
   *     window is 10 points and real Us are commonly 12-20 points long
   *     in slow-rotation sports.
   *   - `_MIN_WIDTH`: never tighten below this width; under 3 points
   *     the maneuver becomes a single bearing sample and downstream
   *     speed/angle metrics get noisy.
   */
  TACK_JIBE_TURN_DIVERGENCE_DEG: 25,
  TACK_JIBE_TURN_CONVERGE_DEG: 22,
  TACK_JIBE_TURN_STABLE_RUN: 2,
  TACK_JIBE_WINDOW_MAX_EXPAND_BACKWARD: 6,
  TACK_JIBE_WINDOW_MAX_EXPAND_FORWARD: 15,
  TACK_JIBE_WINDOW_MIN_WIDTH: 3,

  /**
   * Minimum index separation between two maneuvers of the SAME type and
   * SAME direction before they are considered the same physical turn and
   * merged into one detection.
   *
   * Why this exists:
   *   `parseMovements` slides a `TACK_JIBE_POINTS_TO_USE`-point window
   *   over the track and pushes a candidate whenever the measured
   *   bearing-change angle starts to decrease. After a push it only
   *   advances by ~`POINTS_TO_USE - 1` and resets `maxAngle = 0`, so a
   *   long/slow turn that spans more than one window can be re-detected
   *   from a later window slice — producing 2–3 markers stacked on a
   *   single physical maneuver (visible as clustered "Tack" balloons
   *   on the map).
   *
   *   Two REAL consecutive maneuvers always end on opposite sides
   *   (their recorded `direction` differs), so we disambiguate by also
   *   requiring matching `direction` before merging. Real double-tacks
   *   are therefore never collapsed; only re-detections of the same
   *   physical turn are.
   *
   *   `2 × POINTS_TO_USE` covers up to a 3-detection chain at the
   *   algorithm's natural `POINTS_TO_USE - 1` re-arm gap (positions 0,
   *   ~9, ~18 for points_to_use = 10), which empirically covers slow
   *   wing/wingfoil turns on irregularly-sampled GPX (~1.5–2 s/point).
   */
  TACK_JIBE_MIN_SEPARATION: 20,

  /**
   * "Strong maneuver" threshold (degrees) used to gate neighbor-aware
   * boundary clamping in `filterPhantomManeuvers` (and the symmetric
   * `alignManeuverWindowsToTurn`).
   *
   * The boundary clamp exists so that tight back-to-back REAL maneuvers
   * (e.g. wing/wingfoil slalom) don't have their sustained pre/post leg
   * measurements bleed across the neighbour and average out to "no
   * heading change" — which would silently drop the maneuver. But the
   * SAME clamp also protects two adjacent PHANTOM detections (GPS
   * wiggle on a near-wind-axis straight line) from being filtered:
   * neither can measure its sustained leg across the other, so both
   * abstain and survive.
   *
   * Empirical observation from regression GPX files:
   *   - Real maneuvers detected by the 10-point window always carry an
   *     in-window angle ≥ ~35° (and almost always ≥ 60° for tacks /
   *     ≥ 40° for short wing jibes). 38.7° is the lowest "real" jibe
   *     observed across the test corpus.
   *   - Phantom detections on straight-line wobble carry detected
   *     angles in the 5°-19° range (matching the historical Windsurf
   *     `minAngle: 20` tuning comment).
   *
   * Setting the threshold to 25° comfortably distinguishes the two
   * regimes. Maneuvers below this angle are treated as "weak" for
   * boundary-clamp purposes only: the sustained-heading filter will
   * skip past them when looking for a real anchor neighbour, so two
   * phantoms next to each other can be measured and dropped together.
   * Classification, success, and counts are unaffected.
   */
  TACK_JIBE_STRONG_MANEUVER_ANGLE_DEG: 25,

  // ===== MANEUVER QUALITY THRESHOLDS =====

  /**
   * Quality thresholds for tacks and jibes
   * Used to color-code maneuvers (green/orange/red)
   */
  TACK_QUALITY: {
    GOOD: {
      minAngle: 80,
      maxAngle: 120,
      maxSpeedLoss: 10 // percentage
    },
    MODERATE: {
      minAngle: 70,
      maxAngle: 130,
      maxSpeedLoss: 20 // percentage
    }
    // POOR: everything else (< 70° or > 130°, or > 20% speed loss)
  },

  /**
   * Minimum speeds and angle for a tack/jibe to be flagged "successful"
   * by `Movement.isSuccessful(sport)` in the recognizer. These are
   * post-detection gates only — the upstream classifier in
   * `typeOfMovement()` still uses the global `TACK_JIBE_MIN_ANGLE` (=5°)
   * floor, so changing `minAngle` here never causes the recognizer to
   * miss a candidate, it only changes which candidates we mark
   * `successful` (the UI/markers/stats consider only successful ones).
   *
   * `minAngle = 5` is the no-op default (matches the global floor) and
   * preserves existing behavior — set it higher only when a sport
   * regularly emits sub-20° false positives from GPS wiggle on
   * straight, near-wind-axis segments.
   */
  MIN_SPEEDS_BY_SPORT: {
    // Sailing (sport IDs: 1, 26) — UNCHANGED, intentionally left at the
    // historical thresholds. Sailing uses a half-window bearing
    // measurement upstream which is less prone to small-angle false
    // positives, so the angle gate stays at the global floor.
    SAILING: {
      avgSpeed: 3.0, // km/h
      exitSpeed: 1.5, // km/h
      minAngle: 5 // degrees, = global TACK_JIBE_MIN_ANGLE → no-op
    },
    // Windsurf (sport ID: 2)
    //
    // Loosened from 6.0/3.5 to 4.0/2.0 after a slow-wind Windsurf session
    // (whole-track avg ≈ 7.9 km/h) reported a lot of real tacks/jibes
    // being filtered as "unsuccessful": the recognizer correctly detected
    // them, but the rider stalled the board through the turn so avg/exit
    // dipped below the old gates. The exitSpeed gate is still strong
    // enough to reject crashes (true falls have exit ≈ 0 km/h).
    //
    // `minAngle: 20` added afterwards because the looser speed gates
    // started letting through 8°–19° "tacks" — GPS wiggle on straight,
    // near-wind-axis segments. Windsurf uses third-window edge bearings
    // upstream (see `typeOfMovement`), which measures angle from only
    // the first and last short segments and is therefore noise-prone at
    // sub-20° readings. Real planing tacks normally measure ≥ 25°.
    //
    // This change is intentionally scoped to Windsurf — Sailing,
    // Wing/Kite/Foil thresholds are unchanged.
    WINDSURF: {
      avgSpeed: 4.0,
      exitSpeed: 2.0,
      minAngle: 20
    },
    // Wing (sport ID: 18)
    WING: {
      avgSpeed: 7.0,
      exitSpeed: 3.5,
      minAngle: 5
    },
    // Kite (sport IDs: 4, 23, 24, 25)
    KITE: {
      avgSpeed: 8.0,
      exitSpeed: 4.0,
      minAngle: 5
    },
    // Foil (sport IDs: 10, 14, 15, 16, 28)
    FOIL: {
      avgSpeed: 7.0,
      exitSpeed: 4.0,
      minAngle: 5
    },
    // Default for other sports
    DEFAULT: {
      avgSpeed: 3.5,
      exitSpeed: 1.8,
      minAngle: 5
    }
  },

  // ===== MAP & UI =====

  /**
   * Percentage to zoom out when activating scrolling tool (0-1)
   * 0.6 = zoom out to 60% of original size (shows more context)
   */
  ZOOM_OUT_PERCENTAGE: 0.6,

  /**
   * Overlay colors for different features
   */
  OVERLAY_COLORS: {
    LEGS_UPWIND: "#9C27B0", // Purple
    LEGS_DOWNWIND: "#9C27B0", // Purple
    RUNS: "#4CAF50", // Green
    FOILING: "slateblue",
    TACKS: "black",
    JIBES: "black",
    TACKS_SATELLITE: "#39c1d7", // Light blue
    JIBES_SATELLITE: "#39c1d7"
  },

  // ===== POLAR CHART =====

  /**
   * Size of TWA range in degrees for polar chart grouping
   * Smaller values = more granular data but may have gaps
   * Larger values = smoother curves but less detail
   */
  POLAR_CHART_TWA_RANGE: 1,

  /**
   * Size of TWA range in degrees for performance vs target analysis
   * Larger values = more data points per bucket = higher confidence
   * Used for legs and runs performance comparison
   */
  PERFORMANCE_ANALYSIS_TWA_RANGE: 10,

  /**
   * Size of True Wind Speed (TWS) range in km/h for multi-speed polar charts
   */
  POLAR_CHART_TWS_RANGE: 5,

  // ===== VALIDATION & ERROR HANDLING =====

  /**
   * Sentinel value for invalid/missing bearing
   * -1 is used instead of 0 because 0° is a valid bearing (North)
   */
  INVALID_BEARING: -1,

  /**
   * Duplicate point tolerance for tack/jibe detection
   * Points closer than this (in degrees) are considered duplicates
   */
  DUPLICATE_POINT_TOLERANCE: 0.0,

  // ===== SPORT TYPE IDS =====

  /**
   * Sport type identifiers for different water sports
   */
  SPORT_TYPES: {
    SAILING: [1, 26],
    WINDSURF: [2],
    WING: [18],
    KITE: [4, 23, 24, 25],
    FOIL: [10, 14, 15, 16, 28]
  }
};

// Export individual constants for convenience
export const {
  WINDOW_SIZE,
  MAX_SKIPPED_POINTS,
  MIN_LEG_POINTS,
  MIN_LEG_DURATION_SECONDS,
  MIN_RUN_DURATION_SECONDS,
  DIRECTION_CHANGE_THRESHOLD,
  MIN_SPEED_FOR_BEARING_KMH,
  MIN_SAILABLE_TWA,
  UPWIND_DOWNWIND_THRESHOLD,
  TACK_JIBE_POINTS_TO_USE,
  TACK_JIBE_MIN_ANGLE,
  TACK_JIBE_MAX_ANGLE,
  TACK_JIBE_SUSTAINED_LAG_SEC,
  TACK_JIBE_SUSTAINED_DURATION_SEC,
  TACK_JIBE_SUSTAINED_MIN_ANGLE,
  TACK_JIBE_ARC_VERY_STRONG_DEGREES,
  TACK_JIBE_ARC_VERY_STRONG_MARGIN_DEGREES,
  TACK_JIBE_ARC_STRONG_DEGREES,
  TACK_JIBE_ARC_STRONG_MARGIN_DEGREES,
  TACK_JIBE_ARC_SAILING_VERY_STRONG_DEGREES,
  TACK_JIBE_ARC_SAILING_STRONG_DEGREES,
  TACK_JIBE_MIN_AXIS_APPROACH_DEGREES,
  TACK_JIBE_AXIS_EXEMPT_ANGLE_DEG,
  TACK_JIBE_SUCCESS_ANGLE_EXEMPT_DEG,
  TACK_JIBE_PHANTOM_WEAK_UNVERIFIED_MAX_ANGLE,
  TACK_JIBE_STRAIGHT_LEG_MATCH_DEG,
  TACK_JIBE_STRAIGHT_LEG_MAX_FRACTION,
  TACK_JIBE_TURN_DIVERGENCE_DEG,
  TACK_JIBE_TURN_CONVERGE_DEG,
  TACK_JIBE_TURN_STABLE_RUN,
  TACK_JIBE_WINDOW_MAX_EXPAND_BACKWARD,
  TACK_JIBE_WINDOW_MAX_EXPAND_FORWARD,
  TACK_JIBE_WINDOW_MIN_WIDTH,
  TACK_JIBE_MIN_SEPARATION,
  TACK_JIBE_STRONG_MANEUVER_ANGLE_DEG,
  TACK_QUALITY,
  MIN_SPEEDS_BY_SPORT,
  ZOOM_OUT_PERCENTAGE,
  OVERLAY_COLORS,
  POLAR_CHART_TWA_RANGE,
  POLAR_CHART_TWS_RANGE,
  PERFORMANCE_ANALYSIS_TWA_RANGE,
  INVALID_BEARING,
  DUPLICATE_POINT_TOLERANCE,
  SPORT_TYPES
} = WIND_ANALYSIS_CONSTANTS;

export default WIND_ANALYSIS_CONSTANTS;
